import "./App.css";
import { Navigate, BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Footer from "./components/layout/Footer";
import Header from "./components/layout/Header";
import { Toaster } from "react-hot-toast";
import useUserRoutes from "./components/routes/userRoutes";
import useAdminRoutes from "./components/routes/adminRoutes";
import useDriverRoutes from "./components/routes/DriverRoutes";
import NotFound from "./components/layout/NotFound";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import GoogleMapsAddress from "./components/booking/local/GoogleMapsAddress";
import RideApp from "./components/ride/RideApp";
import { useUpdateUserLocationMutation } from "./redux/api/userApi";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Suspended from "./components/layout/Suspended";

// Full-bleed routes render outside the marketing chrome (no Header/Footer/container).
const isFullBleed = (pathname) =>
  pathname === "/select-address" || pathname === "/ride" || pathname.startsWith("/ride/");

function SuspendedRedirect({ children }) {
  const accountStatus = useSelector((state) => state.auth.user?.accountStatus);
  if (accountStatus === "Suspended") {
    return <Navigate to="/suspended" replace />;
  }
  return children;
}

function Layout() {
  const location = useLocation();
  const fullBleed = isFullBleed(location.pathname);
  return (
    <SuspendedRedirect>
      <>
        {!fullBleed && <Header />}
        <div className={fullBleed ? "d-none" : "container"}>
          <Routes>
            {useUserRoutes()}
            {useAdminRoutes()}
            {useDriverRoutes()}
            {!fullBleed && <Route path="*" element={<NotFound />} />}
          </Routes>
        </div>
      </>
    </SuspendedRedirect>
  );
}

function FooterGate() {
  const { pathname } = useLocation();
  if (isFullBleed(pathname)) return null;
  return <Footer />;
}

function App() {
  const LOCATION_UPDATE_THRESHOLD = 350; // meters
  const LOCATION_UPDATE_INTERVAL = 120 * 1000; // ms

  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [updateUserLocation] = useUpdateUserLocationMutation();
  const [lastLocation, setLastLocation] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const role = user?.role || "user";

  useEffect(() => {
    if (role !== "driver") return;

    let lastUpdateTime = Date.now();

    const getDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371e3;
      const toRadians = (deg) => (deg * Math.PI) / 180;
      const φ1 = toRadians(lat1);
      const φ2 = toRadians(lat2);
      const Δφ = toRadians(lat2 - lat1);
      const Δλ = toRadians(lon2 - lon1);
      const a =
        Math.sin(Δφ / 2) ** 2 +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
      return R * a;
    };

    const handleLocationUpdate = async (latitude, longitude) => {
      const currentTime = Date.now();
      const distanceMoved = lastLocation
        ? getDistance(lastLocation.latitude, lastLocation.longitude, latitude, longitude)
        : Infinity;

      const timeElapsed = currentTime - lastUpdateTime;

      if (distanceMoved > LOCATION_UPDATE_THRESHOLD || timeElapsed > LOCATION_UPDATE_INTERVAL) {
        setLocation({ latitude, longitude });
        setLastLocation({ latitude, longitude });
        lastUpdateTime = currentTime;

        try {
          await updateUserLocation({ latitude, longitude }).unwrap();
          console.log("Location updated successfully");
        } catch (err) {
          console.error("Error updating location:", err);
        }
      }
    };

    if ("geolocation" in navigator) {
      // One-time update immediately on open
      navigator.geolocation.getCurrentPosition(
        (pos) => handleLocationUpdate(pos.coords.latitude, pos.coords.longitude),
        (err) => console.error("Initial geolocation error:", err),
        { enableHighAccuracy: true }
      );

      // Continuous updates while app is open
      const watchId = navigator.geolocation.watchPosition(
        (pos) => handleLocationUpdate(pos.coords.latitude, pos.coords.longitude),
        (err) => console.error("Geolocation watch error:", err),
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      );

      return () => navigator.geolocation.clearWatch(watchId);
    } else {
      console.error("Geolocation not supported in this browser.");
    }
  }, [role, updateUserLocation, lastLocation]);

  return (
    <Router>
      <div className="App">
        <Toaster position="top-center" />
        <Routes>
          <Route
            path="/select-address"
            element={
              <ProtectedRoute>
                <GoogleMapsAddress />
              </ProtectedRoute>
            }
          />
          <Route path="/suspended" element={<Suspended />} />
          <Route
            path="/ride/*"
            element={
              <SuspendedRedirect>
                <RideApp />
              </SuspendedRedirect>
            }
          />
        </Routes>
        <Layout />
        <FooterGate />
      </div>
    </Router>
  );
}

export default App;
